import React, { useMemo } from 'react';
import {
  MapPinHouse,
  Building2,
  Bed,
  Building,
  Layers2,
  ArrowUpFromLine,
  House,
  Grid2x2,
  LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface PropertyType {
  icon: LucideIcon;
  label: string;
  tipoDb: string; // Tipo correspondente no banco de dados
  onClick?: () => void;
}

interface PropertyTypesProps {
  title?: string;
  propertyTypes?: PropertyType[];
  availableTypes?: string[]; // Tipos disponíveis no banco
  isLoading?: boolean;
  onPropertyTypeClick?: (type: PropertyType) => void;
  className?: string;
}

const defaultPropertyTypes: PropertyType[] = [
  { icon: MapPinHouse, label: "Casa", tipoDb: "casa" },
  { icon: Building2, label: "Apartamento", tipoDb: "apartamento" },
  { icon: Bed, label: "Studio", tipoDb: "studio" },
  { icon: Building, label: "Loft", tipoDb: "loft" },
  { icon: Layers2, label: "Sobrado", tipoDb: "sobrado" },
  { icon: ArrowUpFromLine, label: "Cobertura", tipoDb: "cobertura" },
  { icon: House, label: "Casa de condomínio", tipoDb: "casa_condominio" },
  { icon: Grid2x2, label: "Terreno", tipoDb: "terreno" },
];

const PropertyTypes: React.FC<PropertyTypesProps> = ({
  title = "Pesquise por tipo",
  propertyTypes = defaultPropertyTypes,
  availableTypes,
  isLoading = false,
  onPropertyTypeClick,
  className
}) => {
  // Filtrar tipos disponíveis
  const filteredTypes = useMemo(() => {
    if (!availableTypes || availableTypes.length === 0) {
      return []; // Se não há tipos disponíveis, não mostrar nenhum
    }
    
    return propertyTypes.filter(type => 
      availableTypes.includes(type.tipoDb.toLowerCase())
    );
  }, [propertyTypes, availableTypes]);

  const handleClick = (type: PropertyType) => {
    if (type.onClick) {
      type.onClick();
    } else if (onPropertyTypeClick) {
      onPropertyTypeClick(type);
    }
  };

  // Se está carregando, mostrar skeletons
  if (isLoading) {
    return (
      <section className={cn("w-full py-10 overflow-x-hidden", className)}>
        <div className="max-w-[1920px] mx-auto">
          <div className="flex justify-center items-center px-4 lg:px-20 mb-8">
            <h2 className="text-2xl lg:text-[32px] font-black text-gray-text text-center">
              {title}
            </h2>
          </div>
          <div className="w-full flex justify-center">
            <div className="flex items-center gap-3 lg:gap-6 px-4 lg:px-20">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-[62px] w-[140px] rounded-[70px]" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Se não há tipos disponíveis, não renderizar a seção
  if (filteredTypes.length === 0) {
    return null;
  }

  return (
    <section className={cn("w-full py-10 overflow-x-hidden", className)}>
      {/* Container centralizado com max-width */}
      <div className="max-w-[1920px] mx-auto">
        {/* Título centralizado */}
        <div className="flex justify-center items-center px-4 lg:px-20 mb-8">
          <h2 className="text-2xl lg:text-[32px] font-black text-gray-text text-center">
            {title}
          </h2>
        </div>

        {/* Carrossel horizontal com scroll lateral - centralizado */}
        <div className="w-full flex justify-center">
          <div
            className="flex items-center gap-3 lg:gap-6 overflow-x-auto overscroll-x-contain snap-x snap-mandatory pb-2 px-4 lg:px-20 scrollbar-hide"
            role="list"
            aria-label="Tipos de imóveis"
            style={{
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE/Edge
            }}
          >
            {filteredTypes.map(({ icon: Icon, label, tipoDb }) => (
              <button
                key={tipoDb}
                onClick={() => handleClick({ icon: Icon, label, tipoDb })}
                className="shrink-0 snap-start flex px-3 lg:px-4 py-3 items-center gap-3 rounded-[70px] bg-gray-light hover:bg-gray-200 transition-colors"
                role="listitem"
              >
                <div className="flex w-[44px] h-[44px] lg:w-[50px] lg:h-[50px] p-3 justify-center items-center rounded-[25px] bg-white">
                  <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-gray-text" />
                </div>
                <span className="text-sm lg:text-base font-medium text-gray-text pr-2 lg:pr-4 whitespace-nowrap">
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { PropertyTypes, type PropertyTypesProps, type PropertyType };
export default PropertyTypes;